import { alpha, styled } from '@mui/material/styles';
import {
  Box,
  Stack,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, PieChart, Users, BarChart, ShoppingBag, Repeat } from 'lucide-react';
import AccountPopover from './AccountPopover';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;
const APPBAR_MOBILE = 60;
const APPBAR_DESKTOP = 72;

// ── Desktop floating glass bar ──────────────────────────────────────────
const RootStyle = styled(AppBar)(({ theme }) => ({
  boxShadow: 'none',
  backdropFilter: 'saturate(200%) blur(24px)',
  WebkitBackdropFilter: 'saturate(200%) blur(24px)',
  backgroundColor: alpha(theme.palette.background.default, 0.6),
  color: theme.palette.text.primary,
  transition: theme.transitions.create(['width', 'margin', 'background-color', 'border-radius'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  // Mobile: full-width, slim, subtle border bottom
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  // Desktop: floating pill inset from the sidebar
  [theme.breakpoints.up('lg')]: {
    width: `calc(100% - ${DRAWER_WIDTH + 48}px)`,
    margin: theme.spacing(2, 3),
    borderRadius: 20,
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  },
}));

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  minHeight: APPBAR_MOBILE,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  [theme.breakpoints.up('lg')]: {
    minHeight: APPBAR_DESKTOP,
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
}));

// ── Mobile bottom tab bar items ─────────────────────────────────────────
const MOBILE_NAV = [
  { label: 'Dashboard', icon: <PieChart size={22} />, path: '/dashboard/app' },
  { label: 'Users', icon: <Users size={22} />, path: '/dashboard/user-management' },
  { label: 'Analytics', icon: <BarChart size={22} />, path: '/dashboard/analytics' },
  { label: 'Services', icon: <ShoppingBag size={22} />, path: '/dashboard/services' },
  { label: 'Txns', icon: <Repeat size={22} />, path: '/dashboard/transaction' },
];

// ── Styled bottom nav paper ─────────────────────────────────────────────
const BottomBar = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: theme.zIndex.appBar,
  backdropFilter: 'saturate(200%) blur(20px)',
  WebkitBackdropFilter: 'saturate(200%) blur(20px)',
  backgroundColor: alpha(theme.palette.background.paper, 0.85),
  borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  boxShadow: `0 -4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
  borderRadius: '20px 20px 0 0',
  overflow: 'hidden',
  // Only show on mobile/tablet
  [theme.breakpoints.up('lg')]: {
    display: 'none',
  },
}));

// ----------------------------------------------------------------------

export default function DashboardNavbar({ onOpenSidebar }) {
  const { user } = useSelector((state) => state.auth);
  const location = useLocation();
  const navigate = useNavigate();

  // Determine active bottom tab
  const activeTab = MOBILE_NAV.findIndex((item) => location.pathname.startsWith(item.path));

  return (
    <>
      {/* ── Top app bar (both mobile + desktop) ── */}
      <RootStyle>
        <ToolbarStyle>
          {/* Hamburger — mobile only */}
          <IconButton
            onClick={onOpenSidebar}
            sx={{
              mr: 1,
              color: 'text.primary',
              display: { lg: 'none' },
              bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.grey[500], 0.16),
              },
              borderRadius: '50%',
              p: 1,
              transition: 'background-color 0.2s',
            }}
          >
            <Menu size={20} />
          </IconButton>

          {/* Page breadcrumb / greeting — desktop */}
          <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.disabled" fontWeight={500}>
              Admin Portal
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Stack direction="row" alignItems="center" spacing={1.5}>
            {user && (
              <Stack alignItems="flex-end" sx={{ display: { xs: 'none', sm: 'flex' } }}>
                <Typography variant="subtitle2" fontWeight={600} lineHeight={1.2}>
                  {user?.firstName}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'capitalize', lineHeight: 1.2 }}
                >
                  {user?.role}
                </Typography>
              </Stack>
            )}
            <AccountPopover />
          </Stack>
        </ToolbarStyle>
      </RootStyle>

      {/* ── Mobile bottom navigation bar ── */}
      <BottomBar elevation={0}>
        <BottomNavigation
          value={activeTab}
          onChange={(_, newValue) => navigate(MOBILE_NAV[newValue].path)}
          showLabels
          sx={{
            height: 64,
            backgroundColor: 'transparent',
            '& .MuiBottomNavigationAction-root': {
              minWidth: 'auto',
              color: 'text.disabled',
              paddingTop: '10px',
              transition: 'color 0.2s, transform 0.15s',
              '&:hover': {
                color: 'text.secondary',
              },
            },
            '& .MuiBottomNavigationAction-root.Mui-selected': {
              color: 'primary.main',
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '10px',
              fontWeight: 600,
              marginTop: '3px',
              '&.Mui-selected': {
                fontSize: '10px',
              },
            },
          }}
        >
          {MOBILE_NAV.map((item) => (
            <BottomNavigationAction key={item.path} label={item.label} icon={item.icon} disableRipple={false} />
          ))}
        </BottomNavigation>
      </BottomBar>
    </>
  );
}
